import { PointerEvent as ReactPointerEvent, memo } from "react"
import { Box, Tooltip, alpha } from "@mui/material"
import { StaffSchedule } from "../../interfaces"
import { DayColumn } from "../../model/period"
import { normalizeHex, readableTextColor } from "../../model/shiftTypeColor"
import {
  DAY_COL_W,
  ROW_H,
  STROKE_BORDER_W as SW,
  Z,
  dayBorderLeftSx,
  gridBorderColor,
  weekendBg,
} from "./gridGeometry"

/** Фон ячейки с типом смены, который был мягко удалён из справочника. */
const DELETED_TYPE_COLOR = "#bdbdbd"

interface GridCellProps {
  staffId: number
  day: DayColumn
  colIdx: number
  schedule?: StaffSchedule.Schedule
  /** undefined у мягко удалённого типа: запись есть, а типа в справочнике уже нет. */
  shiftType?: StaffSchedule.ShiftType
  inStroke: boolean
  /** Крайние ячейки мазка: только у них рисуются вертикальные стороны рамки. */
  isStrokeStart: boolean
  isStrokeEnd: boolean
  isPending: boolean
  canWrite: boolean
  onPointerDown: (
    staffId: number,
    colIdx: number,
    e: ReactPointerEvent<HTMLElement>,
  ) => void
  onPointerEnter: (staffId: number, colIdx: number) => void
}

export const GridCell = memo(
  ({
    staffId,
    day,
    colIdx,
    schedule,
    shiftType,
    inStroke,
    isStrokeStart,
    isStrokeEnd,
    isPending,
    canWrite,
    onPointerDown,
    onPointerEnter,
  }: GridCellProps) => {
    const title = schedule
      ? `${
          shiftType
            ? `${shiftType.emoji} ${shiftType.name}`
            : `Тип смены удалён (id ${schedule.shift_type_id})`
        } — ${day.key}`
      : day.key

    return (
      <Tooltip title={title} enterDelay={400}>
        <Box
          data-testid={`cell-${staffId}-${day.key}`}
          aria-label={title}
          onPointerDown={
            canWrite
              ? (e: ReactPointerEvent<HTMLElement>) =>
                  onPointerDown(staffId, colIdx, e)
              : undefined
          }
          onPointerEnter={
            canWrite ? () => onPointerEnter(staffId, colIdx) : undefined
          }
          // Фон занятой ячейки — inline style: цвет приходит из данных, а не из темы,
          // и в таком виде он проверяем через toHaveStyle.
          style={
            schedule
              ? {
                  backgroundColor: shiftType
                    ? normalizeHex(shiftType.color)
                    : DELETED_TYPE_COLOR,
                  color: shiftType
                    ? readableTextColor(shiftType.color)
                    : undefined,
                }
              : undefined
          }
          sx={{
            width: DAY_COL_W,
            height: ROW_H,
            flex: "0 0 auto",
            // Ячейка мазка поднимается над соседями: рамка рисуется внутрь (inset),
            // но так её точно не перекрывают границы соседних колонок.
            zIndex: inStroke ? Z.cell + 1 : Z.cell,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            lineHeight: 1,
            userSelect: "none",
            // Ячейку без записи красит тема: выходные заметно темнее, ячейка под мазком
            // подсвечивается акцентом (на закрашенной ячейке фон занят цветом смены).
            bgcolor: schedule
              ? undefined
              : inStroke
                ? (theme) => alpha(theme.palette.primary.main, 0.18)
                : day.isWeekend
                  ? weekendBg
                  : "background.paper",
            borderBottom: "1px solid",
            borderColor: gridBorderColor,
            ...dayBorderLeftSx(day.weekday),
            /**
             * Один box-shadow на все рамки ячейки: маркер «сегодня» (вертикальные засечки)
             * и рамка мазка. Рамка мазка непрерывная на весь диапазон — горизонтальные
             * стороны у каждой ячейки, вертикальные только по краям диапазона.
             */
            boxShadow: (theme) => {
              const shadows: string[] = []
              if (day.isToday) {
                const c = theme.palette.primary.main
                shadows.push(
                  `inset ${SW}px 0 0 0 ${c}`,
                  `inset -${SW}px 0 0 0 ${c}`,
                )
              }
              if (inStroke) {
                const c = theme.palette.primary.main
                shadows.push(
                  `inset 0 ${SW}px 0 0 ${c}`,
                  `inset 0 -${SW}px 0 0 ${c}`,
                )
                if (isStrokeStart) shadows.push(`inset ${SW}px 0 0 0 ${c}`)
                if (isStrokeEnd) shadows.push(`inset -${SW}px 0 0 0 ${c}`)
              }
              return shadows.length ? shadows.join(", ") : "none"
            },
            // «В полёте» — только ячейки дней: залипшим ячейкам opacity < 1 нельзя,
            // она создала бы контекст наложения на элементе-слое (спека, раздел 8).
            opacity: isPending ? 0.6 : 1,
            // cursor наследуется от ScheduleGrid и зависит от активной кисти
          }}
        >
          {shiftType?.short_name ?? ""}
        </Box>
      </Tooltip>
    )
  },
)
GridCell.displayName = "GridCell"
